import {type RefObject, useEffect, useMemo, useRef, useState} from "react"
import {useNavigate, useSearchParams} from "react-router-dom"
import {format} from "date-fns"
import {Info} from "lucide-react"
import {
  editableDraftFields,
  type OtkDocumentDetail,
  type OtkDocumentDraftPatchOperation,
  type OtkNonconformityItemInput,
  type OtkReconstructionItemInput,
  type SignedOtkDocumentPayload,
} from "shared"

import {ConsentDownload} from "@/components/document-editor/ConsentDownload"
import {DocumentPreviewDialog} from "@/components/document-editor/DocumentPreviewDialog"
import {DocumentWizardNav, type WizardSection,} from "@/components/document-editor/DocumentWizardNav"
import {
  type ChapterValidationMessage,
  FormChapterValidationAlert,
} from "@/components/document-editor/FormChapterValidationAlert"
import {NonConformitiesTab} from "@/components/document-editor/NonConformitiesTab"
import {OwnerTab} from "@/components/document-editor/OwnerTab"
import {PhotosTab} from "@/components/document-editor/PhotosTab"
import {ReequipmentTab} from "@/components/document-editor/ReequipmentTab"
import {TestsTab} from "@/components/document-editor/TestsTab"
import {VehicleTab} from "@/components/document-editor/VehicleTab"
import {HeaderDeleteDocumentDialog} from "@/components/documents/HeaderDeleteDocumentDialog"
import {KeyValue} from "@/components/KeyValue"
import {Status} from "@/components/Status"
import {
  getOtkDocumentFilesWithUrl,
  patchOtkDocument,
  retryOtkDocument,
  saveOtkDocumentNonconformities,
  saveOtkDocumentReconstructions,
} from "@/lib/api/documents"
import {
  buildSignedOtkDocumentPayload,
  deriveOutcome,
  emissionModeToEngineType,
  initialNonConformitiesState,
  initialOwnerState,
  initialPhotosState,
  initialReequipmentState,
  initialTestsState,
  initialVehicleState,
  isNonConformitiesTabComplete,
  isOwnerTabComplete,
  isPhotosTabComplete,
  isPhotosTabInvalid,
  isReequipmentTabComplete,
  isTestsTabComplete,
  isVehicleCategoryOutsidePermit,
  isVehicleTabComplete,
  nonConformitiesStateFromDetail,
  type NonConformitiesTabState,
  ownerStateFromDetail,
  type OwnerTabState,
  pendingDocumentFromError,
  type PhotosTabState,
  reequipmentStateFromDetail,
  type ReequipmentTabState,
  testsStateFromDetail,
  type TestsTabState,
  vehicleStateFromDetail,
  type VehicleTabState,
  visiblePhotoCategories
} from "@/lib/documentEditor"
import {permitStatusLabels} from "@/lib/api/subjects"
import {documentStatusLabels} from "@/lib/documents"
import {documentCardSectionsFromStates, printDataFromVehicleState,} from "@/lib/documentPreview"
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover"
import {Button} from "@/components/ui/button"
import {Tabs, TabsContent} from "@/components/ui/tabs"
import {useReadOnly, useSession} from "@/store/session"

type SectionId =
  | "vehicle"
  | "owner"
  | "tests"
  | "photos"
  | "nonconformities"
  | "reequipment"

const SECTION_LABELS: Record<SectionId, string> = {
  vehicle: "Транспортний Засіб",
  owner: "Власник і отримувач",
  tests: "Випробування",
  photos: "Фото",
  nonconformities: "Невідповідності",
  reequipment: "Переобладнання",
}

const SECTION_ORDER: SectionId[] = [
  "vehicle",
  "owner",
  "tests",
  "photos",
  "nonconformities",
  "reequipment",
]

const DEFAULT_SECTION: SectionId = "vehicle"

function isSectionId(value: string | null): value is SectionId {
  return value !== null && (SECTION_ORDER as string[]).includes(value)
}

// --- Autosave PATCH wiring (documents.md's `PATCH /otk/documents/:id`),
// vehicle/owner sections only. Each entry maps a tab-state field to the
// backend's editable draft path (`editableDraftFields` in
// documents-service.ts); a field with no entry here has no backend
// counterpart yet and stays local-only, same as every other tab.

function textOp(path: string, raw: string): OtkDocumentDraftPatchOperation {
  const trimmed = raw.trim()
  return trimmed === ""
    ? { op: "unset", path }
    : { op: "set", path, value: trimmed }
}

function numberOp(path: string, raw: string): OtkDocumentDraftPatchOperation {
  const trimmed = raw.trim()
  const parsed = trimmed === "" ? NaN : Number(trimmed)
  return Number.isFinite(parsed)
    ? { op: "set", path, value: parsed }
    : { op: "unset", path }
}

function dateOp(path: string, raw: string): OtkDocumentDraftPatchOperation {
  return raw === "" ? { op: "unset", path } : { op: "set", path, value: raw }
}

function boolOp(
  path: string,
  value: boolean | null
): OtkDocumentDraftPatchOperation {
  return value === null ? { op: "unset", path } : { op: "set", path, value }
}

function stringArrayOp(
  path: string,
  value: string[]
): OtkDocumentDraftPatchOperation {
  return value.length === 0 ? { op: "unset", path } : { op: "set", path, value }
}

// Array-aware equality so unchanged `purposes` (the one array-valued field)
// doesn't look "changed" on every re-commit — everything else here is a
// primitive, so `===` alone would do for those.
function valuesEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index])
  }
  return a === b
}

const VEHICLE_FIELD_OPS: Partial<{
  [K in keyof VehicleTabState]: (
    value: VehicleTabState[K]
  ) => OtkDocumentDraftPatchOperation
}> = {
  plate: (v) => textOp("vehicle.plateNumber", v),
  vin: (v) => textOp("vehicle.vin", v),
  brand: (v) => textOp("vehicle.brand", v),
  model: (v) => textOp("vehicle.model", v),
  category: (v) => ({ op: "set", path: "vehicle.category", value: v }),
  vehicleType: (v) => textOp("vehicle.type", v),
  ecologyLevel: (v) => textOp("vehicle.emissionLevel", v),
  bodyNumber: (v) => textOp("vehicle.bodyNumber", v),
  chassisNumber: (v) => textOp("vehicle.chassisNumber", v),
  manufactureYear: (v) => numberOp("vehicle.manufactureYear", v),
  registrationDate: (v) => dateOp("vehicle.registrationDate", v),
  currentOdometer: (v) => numberOp("vehicle.currentOdometer", v),
  previousOdometer: (v) => numberOp("vehicle.previousOdometer", v),
  purposes: (v) => stringArrayOp("vehicle.purposes", v),
  isTaxi: (v) => boolOp("isTaxi", v),
}

const OWNER_FIELD_OPS: Partial<{
  [K in keyof OwnerTabState]: (
    value: OwnerTabState[K]
  ) => OtkDocumentDraftPatchOperation
}> = {
  ownerType: (v) => ({ op: "set", path: "owner.type", value: v }),
  fullName: (v) => textOp("owner.fullName", v),
  birthDate: (v) => dateOp("owner.dateOfBirth", v),
  taxId: (v) => textOp("owner.taxId", v),
  orgName: (v) => textOp("owner.organizationName", v),
  edrpou: (v) => textOp("owner.edrpou", v),
  phone: (v) => textOp("owner.phone", v),
  email: (v) => textOp("owner.email", v),
  address: (v) => textOp("owner.postalAddress", v),
  idDocType: (v) => textOp("owner.idDocumentType", v),
  idDocSeries: (v) => textOp("owner.idDocumentSeries", v),
  idDocNumber: (v) => textOp("owner.idDocumentNumber", v),
  idDocIssuedBy: (v) => textOp("owner.idDocumentIssuer", v),
  idDocIssuedAt: (v) => dateOp("owner.idDocumentIssueDate", v),
  recipientMode: (v) => ({
    op: "set",
    path: "receiverIsOwner",
    value: v === "owner",
  }),
  recFullName: (v) => textOp("receiver.fullName", v),
  recBirthDate: (v) => dateOp("receiver.dateOfBirth", v),
  recIdDocType: (v) => textOp("receiver.idDocumentType", v),
  recIdDocSeries: (v) => textOp("receiver.idDocumentSeries", v),
  recIdDocNumber: (v) => textOp("receiver.idDocumentNumber", v),
  recIdDocIssuedBy: (v) => textOp("receiver.idDocumentIssuer", v),
  recIdDocIssuedAt: (v) => dateOp("receiver.idDocumentIssueDate", v),
  receiptDocSeries: (v) => textOp("receiver.powerOfAttorneySeries", v),
  receiptDocNumber: (v) => textOp("receiver.powerOfAttorneyNumber", v),
  receiptDocDate: (v) => dateOp("receiver.powerOfAttorneyDate", v),
}

const TESTS_FIELD_OPS: Partial<{
  [K in keyof TestsTabState]: (
    value: TestsTabState[K]
  ) => OtkDocumentDraftPatchOperation
}> = {
  videoUrl: (v) => textOp("testResults.videoUrl", v),
  totalBrakingForce: (v) => numberOp("testResults.totalSpecificBrakingForce", v),
  imbalanceRatio: (v) => numberOp("testResults.brakeForceImbalanceRatio", v),
  brakeResponseTime: (v) => numberOp("testResults.brakeResponseTime", v),
  parkingBrakeForce: (v) => numberOp("testResults.parkingBrakeSpecificForce", v),
  ovality: (v) => numberOp("testResults.brakeForceOvality", v),
  emissionMode: (v) => ({
    op: "set",
    path: "testResults.engineType",
    value: emissionModeToEngineType[v],
  }),
  coIdle: (v) => numberOp("testResults.coAtIdleRpm", v),
  cnhmIdle: (v) => numberOp("testResults.cnhmAtIdleRpm", v),
  coElevated: (v) => numberOp("testResults.coAtElevatedRpm", v),
  cnhmElevated: (v) => numberOp("testResults.cnhmAtElevatedRpm", v),
  lambda: (v) => numberOp("testResults.lambda", v),
  smokeK: (v) => numberOp("testResults.smokeOpacityK", v),
  windshieldExempt: (v) => ({
    op: "set",
    path: "testResults.windshieldMarked",
    value: v,
  }),
  windshield: (v) => numberOp("testResults.windshield", v),
  leftExempt: (v) => ({ op: "set", path: "testResults.leftSideMarked", value: v }),
  left: (v) => numberOp("testResults.leftSide", v),
  rightExempt: (v) => ({ op: "set", path: "testResults.rightSideMarked", value: v }),
  right: (v) => numberOp("testResults.rightSide", v),
  rearExempt: (v) => ({ op: "set", path: "testResults.rearWindowMarked", value: v }),
  rear: (v) => numberOp("testResults.rearWindow", v),
}

// shared's `editableDraftFields` — the exact schemas `patchDraftDocument`
// (documents-service.ts) validates a `"set"` operation's value against.
// Reused here instead of duplicating VIN/plate regexes etc. a second time.
// An `"unset"` op is never an error (see that file's own note): emptying a
// field is always a valid unset, regardless of what its schema requires
// when a value *is* provided.
function fieldError(operation: OtkDocumentDraftPatchOperation): string | undefined {
  if (operation.op === "unset") return undefined
  const schema = editableDraftFields[operation.path]
  if (!schema) return undefined
  const result = schema.safeParse(operation.value)
  return result.success ? undefined : result.error.issues[0]?.message
}

// Derives one tab's error map straight from its own `*_FIELD_OPS` table, so
// the validated value is always exactly what `commitFields` would send —
// there's no separate "what does this field look like on the wire" logic to
// keep in sync.
function fieldErrors<S>(
  state: S,
  fieldOps: Partial<{ [K in keyof S]: (value: S[K]) => OtkDocumentDraftPatchOperation }>
): Partial<Record<keyof S, string>> {
  const errors: Partial<Record<keyof S, string>> = {}
  for (const key of Object.keys(fieldOps) as (keyof S)[]) {
    const operation = fieldOps[key]?.(state[key])
    const message = operation && fieldError(operation)
    if (message) errors[key] = message
  }
  return errors
}

// `operation.path` is a dot-path into the raw fetched `detail`
// (`"vehicle.category"`, `"isTaxi"`) — this reads it back the same way
// `commitPatch`'s `PATCH` would look it up server-side, to tell "explicitly
// absent" apart from "present with a falsy value" (`false`/`""`/`0`).
function detailHasPath(detail: OtkDocumentDetail, path: string): boolean {
  let cursor: unknown = detail
  for (const segment of path.split(".")) {
    if (cursor === null || typeof cursor !== "object") return false
    cursor = (cursor as Record<string, unknown>)[segment]
  }
  return cursor !== undefined
}

// See the reconciliation effect below for why this exists: a field whose
// seeded value already matches its own UI default never looks "changed" to
// `commitFields`'s diff, so the normal autosave path can permanently skip
// sending it. This re-derives the same operations `commitFields` would
// produce from a full edit, but keyed on "genuinely missing in `detail`"
// instead of "different from the local baseline".
function backfillMissingFields<S>(
  detail: OtkDocumentDetail,
  seeded: S,
  fieldOps: Partial<{ [K in keyof S]: (value: S[K]) => OtkDocumentDraftPatchOperation }>
): OtkDocumentDraftPatchOperation[] {
  const ops: OtkDocumentDraftPatchOperation[] = []
  for (const key of Object.keys(fieldOps) as (keyof S)[]) {
    const operation = fieldOps[key]?.(seeded[key])
    if (!operation || operation.op !== "set" || fieldError(operation)) continue
    if (!detailHasPath(detail, operation.path)) ops.push(operation)
  }
  return ops
}

/**
 * document-editor.md's `DocumentEditor` — used both for creating a new
 * draft and editing an existing one, matching the spec's own "both routes
 * render the exact same component" rule. Mounted exclusively inside
 * `DocumentModal` now: `NewDocumentButton` creates the draft server-side
 * then navigates to `/documents/edit/:id`, and the drafts list's own
 * "Редагувати" row action opens the same route directly; `DocumentModal`
 * always renders this with `embedded`. Field state itself is still purely
 * local (`useState`), never fetched or persisted:
 *
 * - Only the vehicle/owner/tests sections autosave for real: `VEHICLE_FIELD_OPS`/
 *   `OWNER_FIELD_OPS`/`TESTS_FIELD_OPS` above map a tab-state field to its
 *   `editableDraftFields` path in documents-service.ts and `commitPatch`
 *   sends it via `PATCH /otk/documents/:id` (`patchOtkDocument`) — text
 *   fields on blur, select/radio/checkbox/date fields immediately, mirroring
 *   `VehicleTab`/`OwnerTab`/`TestsTab`'s own `onCommit` callback. A field with
 *   no backend counterpart in that map is silently skipped, and the sign
 *   `POST` plus photos remain local-only, same as before.
 *   `commitVehicleFields`/`commitOwnerFields`/`commitTestsFields` diff each
 *   incoming patch against `committedVehicleRef`/`committedOwnerRef`/
 *   `committedTestsRef` (the last value actually sent, seeded from `detail`)
 *   and drop any field that didn't change — e.g. focusing out of an untouched
 *   `plateNumber` sends no request. Nonconformities and reconstructions each
 *   autosave through their own `PUT .../nonconformities` /
 *   `PUT .../reconstructions` endpoint instead, which replaces the whole list
 *   in one call rather than diffing individual fields — `commitNonConformities`/
 *   `commitReequipment` read the latest merged state off `nonConformitiesRef`/
 *   `reequipmentRef` and skip the request while the state can't yet satisfy
 *   the backend's schema (radio unset, empty list, or an "approved"
 *   reconstruction missing its doc name/number/date). The header's "Останні
 *   зміни" / "Збереження..." label follows
 *   `lastSavedAt`, which `commitPatch` alone flips to `null` then back to a
 *   timestamp, so it only ever reflects a real in-flight/completed `PATCH`,
 *   never a keypress in a local-only tab or a no-op commit.
 *   The `documentNumber` shown in the header comes from whatever the caller
 *   already fetched (`DocumentEditModalRoute`'s `getOtkDocumentById`, or the
 *   documents list's own summary row) — this component never fetches one
 *   itself. When the caller passes the fetched `detail` (`OtkDocumentDetail`)
 *   through the new `detail` prop, an effect keyed on `detail.id` seeds every
 *   tab's local state once via `documentEditor.ts`'s `*StateFromDetail`
 *   mappers — vehicle/owner/testResults/nonconformities/reconstructions.
 *   Photos aren't seeded: `OtkDocumentDetail` only carries EDRTZ photo
 *   metadata, not the uploaded file/URL `PhotosTabState` needs.
 * - Reference data that would normally come from the registry
 *   (`nonConformityCodes`, ЄДРТЗ responses, спецпродукція warehouse stock,
 *   print profiles) is real UI wired to empty datasets, not invented mock
 *   rows — see the per-file notes in `components/document-editor/`.
 * - The wizard rail's exact documented visual contract (colors, sizing,
 *   states) is reproduced with plain markup instead of the reui `Stepper`
 *   primitive the spec names, since that package isn't installed in this
 *   app yet — see `DocumentWizardNav.tsx`'s own note.
 * - `cropperjs`'s crop/rotate step and the canvas date-stamp are deferred
 *   (new dependency) — see `PhotosTab.tsx`'s own note.
 *
 * `embedded` mirrors document-editor.md's own prop: true when rendered
 * inside `DocumentModal`'s chrome (that chrome already owns the delete/
 * view-toggle/autosave header for the real, wired document-view flow), so
 * this component then renders only the `KeyValue` metadata row and the
 * wizard body — no H1, no delete button, no autosave text of its own.
 */
export function DocumentEdit({
  embedded = false,
  documentNumber,
  documentId,
  version,
  detail,
  onAutosaveStatusChange,
  onSigned,
}: {
  embedded?: boolean
  documentNumber?: string
  /** Draft id `PATCH /otk/documents/:id` autosave targets; omit to disable autosave (e.g. no document loaded yet). */
  documentId?: string
  /** Draft's current `version`, for the patch's optimistic-concurrency `expectedVersion`. */
  version?: number
  /** Fetched document body (`getOtkDocumentById`); seeds every tab's local state once it arrives. */
  detail?: OtkDocumentDetail | null
  /** Reports `lastSavedAt` up to `DocumentModal`'s own portaled header aside — see document-editor.md's `autosaveStatus`. */
  onAutosaveStatusChange?: (savedAt: Date | null) => void
  /** Bubbles the updated document (new `status`/`version`/`signedAt`) up to whichever route owns `detail`, once `DocumentPreviewDialog`'s sign flow succeeds. */
  onSigned?: (document: OtkDocumentDetail) => void
}) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const readOnly = useReadOnly()
  const userName = useSession((s) => s.user?.name) ?? "—"
  const subject = useSession((s) => s.subject)
  const permit = subject?.permit

  const [createdAt] = useState(() => new Date())
  // `null` reads as "Збереження..." in the header — only ever set right
  // around an actual `PATCH /otk/documents/:id` call (see `commitPatch`),
  // never from local-only field edits, so it can't flicker on every
  // keypress or on a blur that didn't actually change anything.
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(() => new Date())
  useEffect(() => {
    onAutosaveStatusChange?.(lastSavedAt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedAt])

  const [expectedVersion, setExpectedVersion] = useState(version ?? 0)
  useEffect(() => {
    if (version !== undefined) setExpectedVersion(version)
  }, [version])

  // `expectedVersionOverride` exists for the seeding effect below: it fires
  // on the same render where `detail`/`version` first arrive together from
  // the parent's fetch, before the sibling effect's `setExpectedVersion(version)`
  // has actually applied (state updates from an earlier effect in the same
  // pass aren't visible until the next render) — so without the override,
  // that first backfill PATCH would go out with the stale initial
  // `expectedVersion` (0) and get rejected as a version conflict, silently
  // leaving the seeded defaults unsaved server-side.
  async function commitPatch(
    operations: OtkDocumentDraftPatchOperation[],
    expectedVersionOverride?: number
  ) {
    if (!documentId || readOnly || operations.length === 0) return
    const previousSavedAt = lastSavedAt
    setLastSavedAt(null)
    try {
      const updated = await patchOtkDocument(documentId, {
        expectedVersion: expectedVersionOverride ?? expectedVersion,
        operations,
      })
      setExpectedVersion(updated.version)
      setLastSavedAt(new Date())
    } catch (error) {
      console.error("Не вдалося зберегти зміни документа", error)
      setLastSavedAt(previousSavedAt)
    }
  }

  // Baseline the last-committed value of every autosaved field was seen at,
  // so a blur/select that didn't actually change anything (e.g. focusing
  // out of `plateNumber` unchanged) never fires a PATCH. Seeded from
  // `detail` alongside the tab state itself (see the effect below) and
  // advanced only for the fields actually sent, right before the request.
  const committedVehicleRef = useRef<VehicleTabState>(initialVehicleState)
  const committedOwnerRef = useRef<OwnerTabState>(initialOwnerState)
  const committedTestsRef = useRef<TestsTabState>(initialTestsState)
  // Full (not diffed) last-known nonconformities state — the `PUT
  // .../nonconformities` endpoint replaces the whole list in one call, so
  // every commit needs the merged state, not just the changed fields.
  const nonConformitiesRef = useRef<NonConformitiesTabState>(
    initialNonConformitiesState
  )
  // Same full-state-not-diff reasoning as `nonConformitiesRef` — the `PUT
  // .../reconstructions` endpoint also replaces its whole list in one call.
  const reequipmentRef = useRef<ReequipmentTabState>(initialReequipmentState)

  // Shared by all three tabs' commit functions below. A field that fails
  // `fieldError` is left out of both the request and `committedRef` — it
  // stays "changed" so the same edit is retried (and re-validated) on the
  // next commit once the user fixes it, instead of being sent anyway and
  // failing server-side with no explanation.
  function commitFields<S>(
    patch: Partial<S>,
    fieldOps: Partial<{ [K in keyof S]: (value: S[K]) => OtkDocumentDraftPatchOperation }>,
    committedRef: RefObject<S>
  ) {
    const changed: Partial<S> = {}
    for (const [key, fieldValue] of Object.entries(patch as object)) {
      const typedKey = key as keyof S
      if (!valuesEqual(committedRef.current[typedKey], fieldValue)) {
        changed[typedKey] = fieldValue as S[keyof S]
      }
    }
    const operations: OtkDocumentDraftPatchOperation[] = []
    const applied: Partial<S> = {}
    for (const [key, fieldValue] of Object.entries(changed)) {
      const typedKey = key as keyof S
      const operation = fieldOps[typedKey]?.(fieldValue as S[keyof S])
      if (!operation || fieldError(operation)) continue
      operations.push(operation)
      applied[typedKey] = fieldValue as S[keyof S]
    }
    if (operations.length === 0) return
    committedRef.current = { ...committedRef.current, ...applied }
    void commitPatch(operations)
  }

  function commitVehicleFields(patch: Partial<VehicleTabState>) {
    commitFields(patch, VEHICLE_FIELD_OPS, committedVehicleRef)
  }

  function commitOwnerFields(patch: Partial<OwnerTabState>) {
    commitFields(patch, OWNER_FIELD_OPS, committedOwnerRef)
  }

  function commitTestsFields(patch: Partial<TestsTabState>) {
    commitFields(patch, TESTS_FIELD_OPS, committedTestsRef)
  }

  // `found === null` means no radio choice yet — nothing to save. The
  // backend also rejects `hasNonconformities: true` with an empty `items`
  // (`saveNonconformitiesBodySchema`'s superRefine), which happens for the
  // instant between picking "Так" and adding the first code/issue, so that
  // combination is skipped too rather than sent and failed.
  async function commitNonConformities(next: NonConformitiesTabState) {
    if (!documentId || readOnly || next.found === null) return
    const items: OtkNonconformityItemInput[] = next.found
      ? [
          ...next.selectedCodes.map(
            (code): OtkNonconformityItemInput => ({
              form: "STANDARD",
              nonconformityCodeId: code.code,
            })
          ),
          ...next.unlistedIssues.map(
            (issue): OtkNonconformityItemInput => ({
              form: "NONSTANDARD",
              defectType: issue.type,
              description: issue.description,
            })
          ),
        ]
      : []
    if (next.found && items.length === 0) return
    const previousSavedAt = lastSavedAt
    setLastSavedAt(null)
    try {
      const updated = await saveOtkDocumentNonconformities(documentId, {
        expectedVersion,
        hasNonconformities: next.found,
        items,
      })
      setExpectedVersion(updated.version)
      setLastSavedAt(new Date())
    } catch (error) {
      console.error("Не вдалося зберегти невідповідності", error)
      setLastSavedAt(previousSavedAt)
    }
  }

  // Mirrors `commitNonConformities`: reads the latest merged state off
  // `reequipmentRef` (kept current by `updateReequipment`) rather than a
  // stale closure, and skips the request while the radio is unset or the
  // list can't yet satisfy `reconstructionInputSchema` (empty description,
  // or an "approved" row missing its doc name/number/date) — it'll send on
  // the next edit that makes the state valid instead.
  async function commitReequipment() {
    const next = reequipmentRef.current
    if (!documentId || readOnly || next.hasReequipment === null) return
    const items: OtkReconstructionItemInput[] = next.hasReequipment
      ? next.conversions
          .filter((c) => c.description.trim() !== "")
          .map(
            (c): OtkReconstructionItemInput => ({
              approved: c.approved,
              description: c.description.trim(),
              ...(c.docName.trim() === "" ? {} : { documentName: c.docName.trim() }),
              ...(c.docNumber.trim() === ""
                ? {}
                : { documentNumber: c.docNumber.trim() }),
              ...(c.docDate === "" ? {} : { documentDate: c.docDate }),
            })
          )
      : []
    if (next.hasReequipment && items.length === 0) return
    if (
      items.some(
        (item) =>
          item.approved &&
          (!item.documentName || !item.documentNumber || !item.documentDate)
      )
    )
      return
    const previousSavedAt = lastSavedAt
    setLastSavedAt(null)
    try {
      const updated = await saveOtkDocumentReconstructions(documentId, {
        expectedVersion,
        hasReconstructions: next.hasReequipment,
        items,
      })
      setExpectedVersion(updated.version)
      setLastSavedAt(new Date())
    } catch (error) {
      console.error("Не вдалося зберегти переобладнання", error)
      setLastSavedAt(previousSavedAt)
    }
  }

  const sectionParam = searchParams.get("section")
  const activeSection: SectionId = isSectionId(sectionParam)
    ? sectionParam
    : DEFAULT_SECTION

  function setActiveSection(next: SectionId) {
    const params = new URLSearchParams(searchParams)
    params.set("section", next)
    setSearchParams(params)
  }

  const [vehicle, setVehicle] = useState<VehicleTabState>(initialVehicleState)
  const [owner, setOwner] = useState<OwnerTabState>(initialOwnerState)
  const [tests, setTests] = useState<TestsTabState>(initialTestsState)
  const [photos, setPhotos] = useState<PhotosTabState>(initialPhotosState)
  const [nonConformities, setNonConformities] =
    useState<NonConformitiesTabState>(initialNonConformitiesState)
  const [reequipment, setReequipment] = useState<ReequipmentTabState>(
    initialReequipmentState
  )

  // Derived, not stored — recomputed from the live tab state on every
  // render (cheap: a handful of `safeParse` calls) via the same
  // `*_FIELD_OPS` tables `commitFields` sends from, so a field's inline
  // error and what would actually be committed can never disagree.
  const vehicleErrors = useMemo(
    () => fieldErrors(vehicle, VEHICLE_FIELD_OPS),
    [vehicle]
  )
  const ownerErrors = useMemo(() => fieldErrors(owner, OWNER_FIELD_OPS), [owner])
  const testsErrors = useMemo(() => fieldErrors(tests, TESTS_FIELD_OPS), [tests])

  useEffect(() => {
    if (!detail) return
    const seededVehicle = vehicleStateFromDetail(detail)
    const seededOwner = ownerStateFromDetail(detail)
    setVehicle(seededVehicle)
    setOwner(seededOwner)
    committedVehicleRef.current = seededVehicle
    committedOwnerRef.current = seededOwner
    const seededTests = testsStateFromDetail(detail)
    setTests(seededTests)
    committedTestsRef.current = seededTests
    const seededNonConformities = nonConformitiesStateFromDetail(detail)
    setNonConformities(seededNonConformities)
    nonConformitiesRef.current = seededNonConformities
    const seededReequipment = reequipmentStateFromDetail(detail)
    setReequipment(seededReequipment)
    reequipmentRef.current = seededReequipment

    // Any field whose UI default already "looks like an answer" (a `Select`
    // pre-selected to its first/common option — `vehicle.category: "M1"`,
    // `owner.ownerType: "INDIVIDUAL"`, or
    // `tests.emissionMode: "NO_EMISSIONS"`) never looks "changed" to
    // `commitFields`'s
    // diff against the very default it was seeded from. If the operator's
    // real answer happens to match that default — the common case — the
    // PATCH that would actually persist it server-side never fires, even
    // though the submit schema requires the field regardless.
    // Reconciles once per load: any field with a real ("set") value locally
    // but genuinely absent from the raw `detail` gets backfilled
    // immediately, instead of waiting for an edit that may never come.
    const backfill = [
      ...backfillMissingFields(detail, seededVehicle, VEHICLE_FIELD_OPS),
      ...backfillMissingFields(detail, seededOwner, OWNER_FIELD_OPS),
      ...backfillMissingFields(detail, seededTests, TESTS_FIELD_OPS),
    ]
    if (backfill.length > 0) void commitPatch(backfill, version)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id])

  function updateVehicle(patch: Partial<VehicleTabState>) {
    setVehicle((prev) => ({ ...prev, ...patch }))
  }
  function updateOwner(patch: Partial<OwnerTabState>) {
    setOwner((prev) => ({ ...prev, ...patch }))
  }
  function updateTests(patch: Partial<TestsTabState>) {
    setTests((prev) => ({ ...prev, ...patch }))
  }
  function updatePhotos(patch: Partial<PhotosTabState>) {
    setPhotos((prev) => ({ ...prev, ...patch }))
  }
  function updateNonConformities(patch: Partial<NonConformitiesTabState>) {
    const next = { ...nonConformitiesRef.current, ...patch }
    nonConformitiesRef.current = next
    setNonConformities(next)
    void commitNonConformities(next)
  }
  function updateReequipment(patch: Partial<ReequipmentTabState>) {
    const next = { ...reequipmentRef.current, ...patch }
    reequipmentRef.current = next
    setReequipment(next)
  }

  const outcome = useMemo(
    () =>
      deriveOutcome({
        selectedCodes: nonConformities.selectedCodes,
        unlistedIssues: nonConformities.unlistedIssues,
        conversions: reequipment.conversions,
      }),
    [
      nonConformities.selectedCodes,
      nonConformities.unlistedIssues,
      reequipment.conversions,
    ]
  )
  const isAct = outcome.actReasons.length > 0

  const photosComplete = isPhotosTabComplete(
    photos,
    vehicle.category,
    vehicle.hasTachograph
  )
  const photosInvalid = isPhotosTabInvalid(
    photos,
    vehicle.category,
    vehicle.hasTachograph
  )

  // document-editor.md's vehicle-tab invalid state and the FormChapterValidationAlert's
  // "Категорія:" message. The prototype spec frames this per-пункт ("Пункт «...» не має
  // дозволу"); this app's own subject-profile model has one permit for the whole subject,
  // not one per пункт (`OtkSubjectPermit` on `OtkSubjectProfile`, shared/index.ts), so the
  // message below is adapted to that real shape rather than inventing a per-пункт permit
  // that doesn't exist here — flagged as a spec/model divergence, not silently resolved.
  const vehicleCategoryInvalid = isVehicleCategoryOutsidePermit(
    vehicle.category,
    permit
  )
  const vehicleCategoryMessage =
    permit && vehicleCategoryInvalid
      ? permit.status !== "ДІЄ"
        ? `Дозвіл суб'єкта ${permitStatusLabels[permit.status].toLowerCase()} — підписання документів наразі недоступне.`
        : `Категорія: дозвіл суб'єкта не поширюється на категорію ${vehicle.category}. Чернетку зберегти можна, підписати документ для цієї категорії — ні. Дозволені: ${permit.kategorii.join(", ")}.`
      : null

  const wizardSections: (WizardSection & { id: SectionId })[] = [
    {
      id: "vehicle",
      value: "vehicle",
      label: SECTION_LABELS.vehicle,
      complete: isVehicleTabComplete(vehicle),
      invalid: vehicleCategoryInvalid,
    },
    {
      id: "owner",
      value: "owner",
      label: SECTION_LABELS.owner,
      complete: isOwnerTabComplete(owner),
    },
    {
      id: "tests",
      value: "tests",
      label: SECTION_LABELS.tests,
      complete: isTestsTabComplete(tests),
    },
    {
      id: "photos",
      value: "photos",
      label: SECTION_LABELS.photos,
      complete: photosComplete,
      invalid: photosInvalid,
    },
    {
      id: "nonconformities",
      value: "nonconformities",
      label: SECTION_LABELS.nonconformities,
      complete: isNonConformitiesTabComplete(nonConformities),
    },
    {
      id: "reequipment",
      value: "reequipment",
      label: SECTION_LABELS.reequipment,
      complete: isReequipmentTabComplete(reequipment),
    },
  ]

  const incompleteSections = wizardSections.filter(
    (section) => !section.complete && !section.invalid
  )
  // Feeds `DocumentPreviewDialog`'s `disabled`/`disabledReasons` props —
  // read-only is deliberately *not* folded in here: per document-editor.md's
  // merged sign flow, `DocumentPreviewDialog` computes its own
  // `signDisabled`/`signDisabledReasons` (`restricted || readOnly ||
  // disabled`) via its own `useReadOnly()` call, so adding the read-only
  // reason here too would just duplicate it in the rendered list.
  const disabledReasons: string[] = incompleteSections.map(
    (section) =>
      `Не заповнено обов'язкові поля в розділі «${section.label}».`
  )
  if (photosInvalid) {
    disabledReasons.push(
      "Одне з фото містить помилку завантаження — перевірте вкладку «Фото»."
    )
  }
  if (vehicleCategoryMessage) {
    disabledReasons.push(vehicleCategoryMessage)
  }
  const signDisabled = disabledReasons.length > 0
  // A document that has moved past ЧЕРНЕТКА is no longer editable — the
  // wizard's own preview/sign/consent actions below are draft-only, same as
  // the delete action. `!documentId` covers the brand-new-draft instant
  // before the first `getOtkDocumentById` response has arrived.
  const isDraftDocument = !documentId || detail?.status === "DRAFT"
  // Signed but not yet accepted by ЄДРТЗ — the sign dialog stays available
  // for its «Повторити надсилання» panel (`POST /otk/documents/:id/retry`).
  const isSubmissionPending = detail?.status === "SUBMISSION_PENDING"

  async function handleRetry() {
    if (!documentId) return
    try {
      onSigned?.(await retryOtkDocument(documentId))
    } catch (error) {
      // A refused or failed attempt still returns the document with its new
      // `nextRetryAt` — apply it before surfacing the error.
      const pending = pendingDocumentFromError(error)
      if (pending) onSigned?.(pending)
      throw error
    }
  }
  // document-editor.md DE-delete / the "Title-row delete icon presence"
  // states row: absent (not merely disabled) once the subject is
  // read-only, or once a stored document has moved past ЧЕРНЕТКА.
  const canDeleteDocument = !readOnly && isDraftDocument

  const draftNumber = documentNumber ?? "ОТК"
  const documentName = `Документ ${draftNumber}`

  const previewPrintData = printDataFromVehicleState(
    draftNumber,
    vehicle,
    isAct,
    { protocolDate: createdAt, subject, punktId: detail?.punktId }
  )
  const previewSections = documentCardSectionsFromStates({
    vehicle,
    owner,
    tests,
    nonConformities,
    reequipment,
  })

  // `DocumentPreviewDialog`'s `buildSignPayload` — built from the wizard's
  // own *live* tab state (the same source `previewSections`/`previewPrintData`
  // already use), not a stale `detail` snapshot. `files` is fetched fresh
  // here rather than read off `PhotosTabState`: the signed payload needs
  // `contentType`/`etag`, which only the server's own file record carries
  // (see `buildSignedOtkDocumentPayload`'s own note).
  async function buildSignPayload(selection: {
    electronicSeries: string
    electronicNumber: string
    blankSeries?: string
    blankNumber?: string
  }): Promise<SignedOtkDocumentPayload> {
    if (!documentId || !detail?.punktId) {
      throw new Error("Document is not ready for signing")
    }
    const filesResponse = await getOtkDocumentFilesWithUrl(documentId)
    return buildSignedOtkDocumentPayload({
      type: isAct ? "NONCONFORMITY_ACT" : "PROTOCOL",
      result: isAct ? "NEGATIVE" : "POSITIVE",
      punktId: detail.punktId,
      vehicle,
      owner,
      tests,
      nonConformities,
      reequipment,
      files: filesResponse.files,
      electronicDocumentSeries: selection.electronicSeries,
      electronicDocumentNumber: selection.electronicNumber,
      blankSeries: selection.blankSeries,
      blankNumber: selection.blankNumber,
    })
  }

  function handleDelete() {
    navigate("/documents")
  }

  // document-editor.md's `FormChapterValidationAlert` — one consolidated,
  // clickable alert per chapter, above its first `FormCard`.
  const chapterValidationMessages: Partial<Record<SectionId, ChapterValidationMessage[]>> = {
    vehicle: vehicleCategoryMessage
      ? [{ id: "category", text: vehicleCategoryMessage, targetId: "vehicle-category" }]
      : [],
    photos: visiblePhotoCategories(vehicle.category, vehicle.hasTachograph)
      .filter((category) => photos.photos[category.id]?.error)
      .map((category) => ({
        id: category.id,
        text: `Помилка завантаження фото: «${category.label}».`,
        targetId: `photo-${category.id}`,
      })),
  }
  const activeChapterMessages = chapterValidationMessages[activeSection] ?? []

  return (
    <div className="flex flex-col gap-4">
      {!embedded && (
        <div className="mb-6 flex items-start justify-between">
          <h1 className="text-2xl font-semibold">{documentName}</h1>
          <div className="flex flex-col items-end gap-2">
            {canDeleteDocument && (
              <HeaderDeleteDocumentDialog
                documentName={draftNumber}
                onDelete={handleDelete}
              />
            )}
            <p className="text-xs text-muted-foreground">
              {lastSavedAt === null
                ? "Збереження..."
                : `Автозбережено о ${format(lastSavedAt, "HH:mm")}`}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <KeyValue
          label="Статус:"
          value={<Status>{documentStatusLabels[detail?.status ?? "DRAFT"]}</Status>}
        />
        <span className="flex items-center gap-2">
          <KeyValue
            label="Тип документа:"
            value={isAct ? "Акт невідповідності" : "Протокол"}
            valueTone={isAct ? "danger" : "normal"}
          />
          {outcome.actReasons.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button aria-label="Причини зміни типу документа" size="icon-xs" variant="ghost">
                  <Info />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <p className="mb-2 text-sm font-medium">
                  Причини зміни типу документа
                </p>
                <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
                  {outcome.actReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          )}
        </span>
        <KeyValue
          label="Створено:"
          value={`${format(createdAt, "dd.MM.yyyy")} (${userName})`}
        />
        <KeyValue
          label="Останні зміни:"
          value={
            lastSavedAt === null
              ? "збереження..."
              : `${format(lastSavedAt, "dd.MM.yyyy - HH:mm")} (${userName})`
          }
        />
      </div>

      <Tabs
        className="flex flex-col gap-4 lg:flex-row lg:items-start"
        onValueChange={(next) => setActiveSection(next as SectionId)}
        orientation="vertical"
        value={activeSection}
      >
        <DocumentWizardNav
          activeValue={activeSection}
          actions={
            (isDraftDocument || isSubmissionPending) && (
              <>
                {isDraftDocument && <ConsentDownload />}
                <DocumentPreviewDialog
                  buildSignPayload={buildSignPayload}
                  disabled={signDisabled}
                  disabledReasons={disabledReasons}
                  documentId={documentId}
                  isNonconformityAct={isAct}
                  onRetry={handleRetry}
                  onSigned={onSigned}
                  printData={previewPrintData}
                  punkt={detail?.punktId}
                  retryAt={detail?.submission?.nextRetryAt ? Date.parse(detail.submission.nextRetryAt) : undefined}
                  submissionConfirmed={detail?.status === "SENT"}
                  submissionPending={isSubmissionPending}
                  sections={previewSections}
                  triggerLabel="Перегляд, Підпис та Друк"
                  triggerVariant="default"
                />
              </>
            )
          }
          onValueChange={(next) => setActiveSection(next as SectionId)}
          sections={wizardSections}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <h2 className="text-xl font-semibold">
            {SECTION_LABELS[activeSection]}
          </h2>

          <FormChapterValidationAlert messages={activeChapterMessages} />

          {SECTION_ORDER.map((section) => (
            <TabsContent key={section} value={section}>
              {section === "vehicle" && (
                <VehicleTab
                  categoryInvalid={vehicleCategoryInvalid}
                  errors={vehicleErrors}
                  onChange={updateVehicle}
                  onCommit={commitVehicleFields}
                  value={vehicle}
                />
              )}
              {section === "owner" && (
                <OwnerTab
                  errors={ownerErrors}
                  onChange={updateOwner}
                  onCommit={commitOwnerFields}
                  value={owner}
                />
              )}
              {section === "tests" && (
                <TestsTab
                  errors={testsErrors}
                  onChange={updateTests}
                  onCommit={commitTestsFields}
                  value={tests}
                />
              )}
              {section === "photos" && (
                <PhotosTab
                  subjectId={detail?.subjectId}
                  documentId={detail?.id}
                  hasTachograph={vehicle.hasTachograph}
                  onChange={updatePhotos}
                  value={photos}
                  vehicleCategory={vehicle.category}
                />
              )}
              {section === "nonconformities" && (
                <NonConformitiesTab
                  onChange={updateNonConformities}
                  value={nonConformities}
                />
              )}
              {section === "reequipment" && (
                <ReequipmentTab
                  onChange={updateReequipment}
                  onCommit={commitReequipment}
                  value={reequipment}
                />
              )}
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  )
}
